import * as React from 'react'

import { Plus } from 'lucide-react'

import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import { Button } from '~/client/src/shared/components/Button/Button'
import MeCompanyQuickFilters, {
  IMeCompanyQuickFiltersProps,
} from '~/client/src/shared/components/Filters/MeCompanyQuickFilters/MeCompanyQuickFilters'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import {
  IViewOptionsPanelProps,
  ViewOptionsPopover,
} from '~/client/src/shared/components/ViewOptions'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IResourceControlsLabels } from './types'

interface IProps {
  searchKey: string
  onSearchChange: (value: string) => void
  filterProps: React.ComponentProps<typeof TableFilters> | null
  quickFilterProps: IMeCompanyQuickFiltersProps
  viewOptionsProps: IViewOptionsPanelProps
  totalCount: number
  selectedCount: number
  countTotal: IResourceControlsLabels['countTotal']
  collapseToggle?: React.ReactNode
  selectionBar?: React.ReactNode
  onCreateNew: () => void
}

function ResourceControlsToolbar({
  searchKey,
  onSearchChange,
  filterProps,
  quickFilterProps,
  viewOptionsProps,
  totalCount,
  selectedCount,
  countTotal,
  collapseToggle,
  selectionBar,
  onCreateNew,
}: IProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:py-2">
      <div className="tw:-mx-4 tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-4 tw:pb-2">
        <SearchInput value={searchKey} onChange={onSearchChange} />
        {filterProps && (
          <TableFilters {...filterProps}>
            <MeCompanyQuickFilters {...quickFilterProps} />
          </TableFilters>
        )}
        <div className="tw:flex-1" />
        <ViewOptionsPopover {...viewOptionsProps} />
      </div>
      <div className="tw:flex tw:items-center tw:gap-2">
        {collapseToggle && (
          // Align the toggle with the table's 40px select (checkbox) column:
          // cancel the toolbar's px-4 inset and center within a 40px slot.
          <div className="tw:-ml-4 tw:flex tw:w-10 tw:shrink-0 tw:items-center tw:justify-center">
            {collapseToggle}
          </div>
        )}
        <span className="tw:shrink-0 tw:text-body-md tw:whitespace-nowrap tw:text-muted-foreground">
          {selectedCount > 0
            ? Localization.translator.xOfYSelected(selectedCount, totalCount)
            : countTotal(totalCount)}
        </span>
        {/* Bulk-edit region: the buttons + Delete stay on one line; when crowded
            the overflow collapses into the bar's own "More" menu instead of
            scrolling. min-w-0 lets this region absorb the shrink so the Create
            button keeps its natural size and stays visible. */}
        <div className="tw:min-w-0 tw:flex-1 tw:overflow-hidden">
          {selectionBar}
        </div>
        <Button variant="default" size="lg" onClick={onCreateNew}>
          <Plus aria-hidden="true" />
          {Localization.translator.create}
        </Button>
      </div>
    </div>
  )
}

export default ResourceControlsToolbar
